<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { ArrowLeft, ChevronDown } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger
  } from '@/components/ui/alert-dialog'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import SortableTableHeader from '@/components/admin/SortableTableHeader.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const router = useRouter()
  const route = useRoute()

  const { t } = useTranslation({
    en: {
      back: 'Back to Case Overview',
      selectDuplicateCase: 'Select duplicate case',
      selectDuplicateCaseDescription:
        'Choose the case you believe duplicates from AI-recommended duplicate cases, to begin the merging process.',
      name: 'Name',
      received: 'Received',
      lastActivity: 'Last Activity',
      lawField: 'Law field',
      lawTopic: 'Topic',
      clerk: 'Clerk',
      note: 'Note',
      cancel: 'Cancel',
      mergeDuplicateCases: 'Merge Duplicate Cases',
      mergeCases: 'Merge Cases',
      mergeDialogTitle: 'Merge requests and remove duplicates',
      mergeDialogDescription:
        'After merging, duplicate requests will be permanently deleted and cannot be restored. All selected information will be retained.',
      mergeDialogNote:
        'After merging, go to the existing requests to confirm the update of protected fields such as phone numbers and email addresses.',
      caseSummary: 'Case summary',
      clientInformation: 'Client Information',
      opponentInformation: 'Opponent Information',
      salutation: 'Salutation:',
      firstName: 'First name:',
      lastName: 'Last name:',
      dateOfBirth: 'Date of birth:',
      phone: 'Phone:',
      email: 'Email:',
      street: 'Street:',
      streetNr: 'Street No.:',
      postalCode: 'Postal code:',
      city: 'City:',
      ai: 'AI'
    },
    de: {
      back: 'Zurück zur Fallübersicht',
      selectDuplicateCase: 'Duplikat-Fall auswählen',
      selectDuplicateCaseDescription:
        'Wählen Sie den Fall aus, von dem Sie glauben, dass er aus den von der KI empfohlenen Duplikatfällen dupliziert wurde, um den Zusammenführungsprozess zu beginnen.',
      name: 'Name',
      received: 'Eingegangen',
      lastActivity: 'Letzte Aktivität',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Thema',
      clerk: 'Sachbearbeiter',
      note: 'Notiz',
      cancel: 'Abbrechen',
      mergeDuplicateCases: 'Duplikatfälle zusammenführen',
      mergeCases: 'Fälle zusammenführen',
      mergeDialogTitle: 'Anfragen zusammenführen und Duplikate entfernen',
      mergeDialogDescription:
        'Nach dem Zusammenführen werden doppelte Anfragen dauerhaft gelöscht und können nicht wiederhergestellt werden. Alle ausgewählten Informationen bleiben erhalten.',
      mergeDialogNote:
        'Gehen Sie nach dem Zusammenführen zu den bestehenden Anfragen, um die Aktualisierung geschützter Felder wie Telefonnummern und E-Mail-Adressen zu bestätigen.',
      caseSummary: 'Fallzusammenfassung',
      clientInformation: 'Mandanteninformationen',
      opponentInformation: 'Gegnerinformationen',
      salutation: 'Anrede:',
      firstName: 'Vorname:',
      lastName: 'Nachname:',
      dateOfBirth: 'Geburtsdatum:',
      phone: 'Telefon:',
      email: 'E-Mail:',
      street: 'Straße:',
      streetNr: 'Hausnummer:',
      postalCode: 'Postleitzahl:',
      city: 'Stadt:',
      ai: 'KI'
    }
  })

  const selectedCaseUuid = ref<string>('')
  const expandedRowUuid = ref<string | null>(null)
  const sortBy = ref<string>('-received_date')

  const sourceCaseUuid = ref<string>('')
  const isButtonDisabled = computed(() => !selectedCaseUuid.value)

  // Mock data for duplicate cases
  interface MockCase {
    uuid: string
    client_name: string
    received_date: string
    last_activity: string
    legal_field: string
    legal_topic: string
    case_worker: {
      first_name: string
      last_name: string
      initials: string
    } | null
    note: string
    case_summary: string
    client_info: {
      salutation: string
      first_name: string
      last_name: string
      date_of_birth: string
      phone: string
      email: string
      street: string
      street_nr: string
      postal_code: string
      city: string
    }
    opponent_info: {
      salutation: string
      first_name: string
      last_name: string
      date_of_birth: string
      phone: string
      email: string
      street: string
      street_nr: string
      postal_code: string
      city: string
    }
  }

  const mockCases = ref<MockCase[]>([
    {
      uuid: '1',
      client_name: 'M. Müller',
      received_date: '26.09.2024',
      last_activity:
        'vor 2 Wochen: Das Dokument für den Personalfragebogen ist angekommen. Wir werden es jetzt bearbeiten. Vielen Dank für Ihre Geduld!',
      legal_field: 'Familienrecht',
      legal_topic: 'Ehevertrag',
      case_worker: {
        first_name: 'Olha',
        last_name: 'Kirdiaieva',
        initials: 'OK'
      },
      note: 'In a recent landmark case, attorney Sarah Mitchell successfully argued for the rights of whistleblowers, setting a new precedent in employment law. This ruling not only protects employees but also encourages transparency in corporate practices.',
      case_summary:
        "In the case of Benjamin Müller, the court examined the implications of negligence in a personal injury claim. The plaintiff, Mr. Smith, alleged that Mr. Johnson's failure to maintain safe premises led to an accident resulting in significant injuries. The court ruled in favor of the plaintiff, emphasizing the duty of care owed by property owners to ensure the safety of visitors.",
      client_info: {
        salutation: 'Mr.',
        first_name: 'Benjamin',
        last_name: 'Müller',
        date_of_birth: '10.01.1998',
        phone: '+49 123 456789',
        email: 'b.muller@example.com',
        street: 'Hauptstraße',
        street_nr: '123',
        postal_code: '10115',
        city: 'Berlin'
      },
      opponent_info: {
        salutation: 'Ms.',
        first_name: 'Sarah',
        last_name: 'Wagner',
        date_of_birth: '22.07.1995',
        phone: '+49 321 654987',
        email: 's.wagner@example.com',
        street: 'Friedrichstraße',
        street_nr: '88',
        postal_code: '10117',
        city: 'Berlin'
      }
    },
    {
      uuid: '2',
      client_name: 'J. Schmidt',
      received_date: '15.10.2024',
      last_activity: 'vor 1 Woche: Neues Dokument hochgeladen',
      legal_field: 'Arbeitsrecht',
      legal_topic: 'Kündigung',
      case_worker: {
        first_name: 'Max',
        last_name: 'Mustermann',
        initials: 'MM'
      },
      note: 'Client requested urgent review of termination documents.',
      case_summary:
        'This employment law case involves a wrongful termination claim. The employee alleges discrimination and seeks reinstatement along with compensation for lost wages and emotional distress.',
      client_info: {
        salutation: 'Ms.',
        first_name: 'Julia',
        last_name: 'Schmidt',
        date_of_birth: '15.05.1985',
        phone: '+49 987 654321',
        email: 'j.schmidt@example.com',
        street: 'Berliner Str.',
        street_nr: '45',
        postal_code: '80331',
        city: 'München'
      },
      opponent_info: {
        salutation: 'Mr.',
        first_name: 'Michael',
        last_name: 'Bauer',
        date_of_birth: '03.12.1978',
        phone: '+49 765 432109',
        email: 'm.bauer@example.com',
        street: 'Maximilianstraße',
        street_nr: '22',
        postal_code: '80539',
        city: 'München'
      }
    },
    {
      uuid: '3',
      client_name: 'A. Weber',
      received_date: '03.11.2024',
      last_activity: 'vor 3 Tagen: Email von Mandant erhalten',
      legal_field: 'Vertragsrecht',
      legal_topic: 'Kaufvertrag',
      case_worker: null,
      note: 'Waiting for additional documentation from client.',
      case_summary:
        'Contract dispute regarding a purchase agreement. The buyer claims breach of warranty and seeks damages for defective goods delivered by the seller.',
      client_info: {
        salutation: 'Mr.',
        first_name: 'Andreas',
        last_name: 'Weber',
        date_of_birth: '22.11.1992',
        phone: '+49 555 123456',
        email: 'a.weber@example.com',
        street: 'Goethestraße',
        street_nr: '78',
        postal_code: '60313',
        city: 'Frankfurt'
      },
      opponent_info: {
        salutation: 'Ms.',
        first_name: 'Laura',
        last_name: 'Hoffmann',
        date_of_birth: '18.09.1988',
        phone: '+49 666 789123',
        email: 'l.hoffmann@example.com',
        street: 'Kaiserstraße',
        street_nr: '55',
        postal_code: '60329',
        city: 'Frankfurt'
      }
    },
    {
      uuid: '4',
      client_name: 'T. Fischer',
      received_date: '20.10.2024',
      last_activity: 'vor 5 Tagen: Termin vereinbart',
      legal_field: 'Mietrecht',
      legal_topic: 'Mietminderung',
      case_worker: {
        first_name: 'Anna',
        last_name: 'Becker',
        initials: 'AB'
      },
      note: 'Initial consultation scheduled for next week.',
      case_summary:
        'Tenant rights case involving rent reduction claims due to property defects. The tenant seeks compensation for uninhabitable conditions and requests immediate repairs.',
      client_info: {
        salutation: 'Ms.',
        first_name: 'Tina',
        last_name: 'Fischer',
        date_of_birth: '08.03.1990',
        phone: '+49 444 789012',
        email: 't.fischer@example.com',
        street: 'Schillerplatz',
        street_nr: '12',
        postal_code: '20095',
        city: 'Hamburg'
      },
      opponent_info: {
        salutation: 'Mr.',
        first_name: 'Thomas',
        last_name: 'Klein',
        date_of_birth: '14.06.1982',
        phone: '+49 777 456789',
        email: 't.klein@example.com',
        street: 'Reeperbahn',
        street_nr: '99',
        postal_code: '20359',
        city: 'Hamburg'
      }
    }
  ])

  const sortedCases = computed(() => {
    const cases = [...mockCases.value]
    const field = sortBy.value.replace('-', '')
    const isDesc = sortBy.value.startsWith('-')

    cases.sort((a, b) => {
      let aVal: string
      let bVal: string

      switch (field) {
        case 'client_name':
          aVal = a.client_name
          bVal = b.client_name
          break
        case 'received_date':
          aVal = a.received_date
          bVal = b.received_date
          break
        case 'last_activity':
          aVal = a.last_activity
          bVal = b.last_activity
          break
        case 'case_worker':
          aVal = a.case_worker ? `${a.case_worker.first_name} ${a.case_worker.last_name}` : ''
          bVal = b.case_worker ? `${b.case_worker.first_name} ${b.case_worker.last_name}` : ''
          break
        default:
          return 0
      }

      if (aVal < bVal) return isDesc ? 1 : -1
      if (aVal > bVal) return isDesc ? -1 : 1
      return 0
    })

    return cases
  })

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    sortBy.value = direction === 'desc' ? `-${field}` : field
  }

  const handleMerge = () => {
    if (!selectedCaseUuid.value) return
    console.log('Merging case into:', selectedCaseUuid.value)
    // TODO: Implement actual merge logic when API is ready
  }

  const handleBack = () => {
    router.push(`/app/cases/legacy/${sourceCaseUuid.value}`)
  }

  const handleCancel = () => {
    // ToDo: Reject the duplicate case and mark as rejected
    handleBack()
  }

  const isExpanded = (caseUuid: string) => {
    return expandedRowUuid.value === caseUuid
  }

  const toggleExpanded = (caseUuid: string) => {
    if (isExpanded(caseUuid)) {
      expandedRowUuid.value = null
    } else {
      expandedRowUuid.value = caseUuid
    }
  }

  const getInfoFieldsColumns = (info: MockCase['client_info']) => {
    return [
      [
        { label: t('salutation'), value: info.salutation },
        { label: t('lastName'), value: info.last_name },
        { label: t('street'), value: info.street },
        { label: t('postalCode'), value: info.postal_code },
        { label: t('email'), value: info.email }
      ],
      [
        { label: t('firstName'), value: info.first_name },
        { label: t('dateOfBirth'), value: info.date_of_birth },
        { label: t('streetNr'), value: info.street_nr },
        { label: t('city'), value: info.city },
        { label: t('phone'), value: info.phone }
      ]
    ]
  }

  onMounted(() => {
    sourceCaseUuid.value = route.params.uuid as string
  })
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen bg-gray-50">
      <div class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <button
          class="mb-4 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900"
          @click="handleBack"
        >
          <ArrowLeft class="h-5 w-5" />
          {{ t('back') }}
        </button>
        <h1 class="mb-2 text-xl font-semibold text-neutral-900">{{ t('mergeDuplicateCases') }}</h1>

        <!-- Table Card -->
        <div class="overflow-hidden rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
          <div class="mb-6">
            <h1 class="mb-2 text-lg font-semibold text-neutral-900">
              {{ t('selectDuplicateCase') }}
            </h1>
            <p class="text-sm text-neutral-500">
              {{ t('selectDuplicateCaseDescription') }}
            </p>
          </div>
          <RadioGroup v-model="selectedCaseUuid">
            <Table>
              <TableHeader>
                <TableRow class="h-14 border-b border-neutral-200 bg-neutral-50/50">
                  <TableHead class="w-[40px]"></TableHead>
                  <SortableTableHeader
                    field="client_name"
                    :current-sort="sortBy"
                    class="w-[180px] min-w-[120px] text-sm font-semibold text-neutral-800"
                    @sort="handleSort"
                  >
                    {{ t('name') }}
                  </SortableTableHeader>
                  <SortableTableHeader
                    field="received_date"
                    :current-sort="sortBy"
                    class="w-[140px] min-w-[100px] text-sm font-semibold text-neutral-800"
                    @sort="handleSort"
                  >
                    {{ t('received') }}
                  </SortableTableHeader>
                  <SortableTableHeader
                    field="last_activity"
                    :current-sort="sortBy"
                    class="w-[200px] min-w-[150px] text-sm font-semibold text-neutral-800"
                    @sort="handleSort"
                  >
                    {{ t('lastActivity') }}
                  </SortableTableHeader>
                  <TableHead class="w-[160px] min-w-[120px] text-sm font-semibold text-neutral-800">
                    <div>{{ t('lawField') }} /</div>
                    <div>{{ t('lawTopic') }}</div>
                  </TableHead>
                  <SortableTableHeader
                    field="case_worker"
                    :current-sort="sortBy"
                    class="w-[200px] min-w-[160px] text-sm font-semibold text-neutral-800"
                    @sort="handleSort"
                  >
                    {{ t('clerk') }}
                  </SortableTableHeader>
                  <TableHead class="w-[250px] min-w-[200px] text-sm font-semibold text-neutral-800">
                    {{ t('note') }}
                  </TableHead>
                  <TableHead
                    class="w-[50px] min-w-[50px] text-sm font-semibold text-neutral-800"
                  ></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <template
                  v-for="caseItem in sortedCases"
                  :key="caseItem.uuid"
                >
                  <TableRow
                    class="h-14 border-b border-neutral-100 hover:bg-neutral-50/50"
                    :class="{ 'bg-gray-50 hover:bg-gray-50': isExpanded(caseItem.uuid) }"
                  >
                    <!-- Radio button -->
                    <TableCell class="py-3">
                      <RadioGroupItem
                        :value="caseItem.uuid"
                        class="h-5 w-5"
                      />
                    </TableCell>

                    <!-- Name -->
                    <TableCell class="max-w-[180px] py-3">
                      <span class="truncate text-sm font-medium text-neutral-900">
                        {{ caseItem.client_name }}
                      </span>
                    </TableCell>

                    <!-- Received Date -->
                    <TableCell class="whitespace-nowrap py-3 text-sm text-neutral-700">
                      {{ caseItem.received_date }}
                    </TableCell>

                    <!-- Last Activity -->
                    <TableCell class="max-w-[200px] py-3">
                      <div class="truncate text-sm text-neutral-700">
                        {{ caseItem.last_activity }}
                      </div>
                    </TableCell>

                    <!-- Law Field / Topic -->
                    <TableCell class="max-w-[160px] py-1.5 text-sm">
                      <div class="truncate text-neutral-800">
                        {{ caseItem.legal_field }}
                      </div>
                      <div class="truncate text-neutral-800">
                        {{ caseItem.legal_topic }}
                      </div>
                    </TableCell>

                    <!-- Case Worker (Sachbearbeiter) -->
                    <TableCell class="w-[200px] max-w-[200px] py-3">
                      <div
                        v-if="caseItem.case_worker"
                        class="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-neutral-200 bg-white pl-1
                          pr-2.5"
                      >
                        <div
                          class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
                            text-white"
                        >
                          {{ caseItem.case_worker.initials }}
                        </div>
                        <span class="truncate text-sm text-neutral-900">
                          {{ caseItem.case_worker.first_name }} {{ caseItem.case_worker.last_name }}
                        </span>
                      </div>
                      <span
                        v-else
                        class="text-sm text-neutral-400"
                      >
                        -
                      </span>
                    </TableCell>

                    <!-- Note -->
                    <TableCell class="max-w-[250px] py-2">
                      <div class="line-clamp-2 h-10 text-sm text-neutral-800">
                        {{ caseItem.note }}
                      </div>
                    </TableCell>

                    <TableCell class="w-[50px]">
                      <button
                        class="flex h-full w-full items-center justify-center text-neutral-500 hover:text-neutral-900"
                        @click="toggleExpanded(caseItem.uuid)"
                      >
                        <ChevronDown
                          class="h-6 w-6 transition-transform"
                          :class="{ 'rotate-180': isExpanded(caseItem.uuid) }"
                        />
                      </button>
                    </TableCell>
                  </TableRow>

                  <!-- Expanded Row Content -->
                  <TableRow
                    v-if="isExpanded(caseItem.uuid)"
                    class="border-b border-neutral-100 bg-gray-50 hover:bg-gray-50"
                  >
                    <TableCell
                      class="p-3"
                      colspan="8"
                    >
                      <div class="mb-6 flex gap-6">
                        <!-- Client Information -->
                        <div class="flex-1">
                          <h3 class="mb-2 text-sm font-medium text-neutral-900">
                            {{ t('clientInformation') }}
                          </h3>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <div class="grid grid-cols-2 gap-4">
                              <div
                                v-for="(column, colIndex) in getInfoFieldsColumns(
                                  caseItem.client_info
                                )"
                                :key="colIndex"
                                class="flex flex-col gap-4"
                              >
                                <div
                                  v-for="field in column"
                                  :key="field.label"
                                  class="flex gap-2 text-sm"
                                >
                                  <span class="font-medium text-neutral-900">
                                    {{ field.label }}
                                  </span>
                                  <span class="text-neutral-700">{{ field.value }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Opponent Information -->
                        <div class="flex-1">
                          <h3 class="mb-2 text-sm font-medium text-neutral-900">
                            {{ t('opponentInformation') }}
                          </h3>
                          <div class="rounded-lg bg-gray-100 p-4">
                            <div class="grid grid-cols-2 gap-4">
                              <div
                                v-for="(column, colIndex) in getInfoFieldsColumns(
                                  caseItem.opponent_info
                                )"
                                :key="colIndex"
                                class="flex flex-col gap-4"
                              >
                                <div
                                  v-for="field in column"
                                  :key="field.label"
                                  class="flex gap-2 text-sm"
                                >
                                  <span class="font-medium text-neutral-900">
                                    {{ field.label }}
                                  </span>
                                  <span class="text-neutral-700">{{ field.value }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Case Summary -->
                      <div>
                        <div class="mb-2 flex items-center gap-2">
                          <h3 class="flex items-center gap-2 text-sm font-medium text-neutral-900">
                            {{ t('caseSummary') }}
                          </h3>
                          <span
                            class="flex h-5 w-5 items-center justify-center rounded-sm bg-purple-200 p-1 text-xs font-semibold
                              text-purple-700"
                          >
                            {{ t('ai') }}
                          </span>
                        </div>
                        <div class="rounded-lg bg-gray-100 p-4">
                          <p class="text-sm text-neutral-700">{{ caseItem.case_summary }}</p>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </template>
              </TableBody>
            </Table>
          </RadioGroup>
        </div>

        <!-- Action Buttons -->
        <div class="mt-6 flex justify-end gap-3">
          <Button
            variant="secondary"
            @click="handleCancel"
          >
            {{ t('cancel') }}
          </Button>

          <AlertDialog>
            <AlertDialogTrigger as-child>
              <Button :disabled="isButtonDisabled">
                {{ t('mergeCases') }}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{{ t('mergeDialogTitle') }}</AlertDialogTitle>
                <AlertDialogDescription class="text-sm font-normal text-neutral-800">
                  {{ t('mergeDialogDescription') }}
                </AlertDialogDescription>
                <ul class="list-disc pl-5 text-sm font-normal text-neutral-800">
                  <li>{{ t('mergeDialogNote') }}</li>
                </ul>
              </AlertDialogHeader>
              <AlertDialogFooter class="flex gap-4">
                <AlertDialogCancel class="flex-1">{{ t('cancel') }}</AlertDialogCancel>
                <AlertDialogAction
                  class="flex-1 bg-red-600 text-white hover:bg-red-700"
                  @click="handleMerge"
                >
                  {{ t('mergeCases') }}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>
